import {
  ADMIN_RECEIVABLES_LOCATION,
  Button,
  Div,
  NoticeBanner,
  UserRole,
  displayCentsInDollars,
  useAuth,
} from '@vgrubs/components';
import { LocationOutstandingSummary } from 'components/src/types/location.types';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CallSummaryModal } from '../../Receivables/components/CallSummaryModal';
import {
  SERVICE_LABELS,
  formatDay,
} from '../../Receivables/helpers/receivablesLabels';
import { useCollectionLetter } from '../../Receivables/hooks/useCollectionLetter';
import { useCollectionLetterSummary } from '../../Receivables/hooks/useCollectionLetterSummary';

/** The parts of the debt, in the order the Outstanding Balances page names them. */
const PARTS: [keyof LocationOutstandingSummary, string][] = [
  ['previousTransactionDueBalance', 'Previous Transaction Due Balance'],
  ['reserveUsed', 'vDrive reserve used'],
  ['negativeFeeDue', 'Negative balance fees'],
  ['saas', 'SaaS'],
];

/** "Previous Transaction Due Balance $4,200.00 · SaaS $1,317.80" */
const breakdownOf = (outstanding: LocationOutstandingSummary) =>
  PARTS.filter(([key]) => (outstanding[key] as number) > 0)
    .map(
      ([key, label]) =>
        `${label} ${displayCentsInDollars(outstanding[key] as number)}`
    )
    .join(' · ');

/** Since when, the plans repaying it, a bank payment on its way, who shares it. */
const notesOf = (
  outstanding: LocationOutstandingSummary,
  locationId: number
) => {
  const others = outstanding.vDriveBalanceLocations.filter(
    (location) => location.id !== locationId
  );
  return [
    outstanding.oldestItemAt &&
      `Oldest item ${formatDay(outstanding.oldestItemAt)}`,
    ...outstanding.activePlans.map(
      (plan) =>
        `${SERVICE_LABELS[plan.service]} repayment plan, ${displayCentsInDollars(
          plan.remaining
        )} left`
    ),
    outstanding.pendingBankTotal > 0 &&
      `${displayCentsInDollars(outstanding.pendingBankTotal)} on its way by bank`,
    others.length > 0 &&
      `Shared vDrive balance with: ${others
        .map((location) => location.name)
        .join(', ')}`,
  ]
    .filter(Boolean)
    .join(' · ');
};

type Props = {
  locationId: number;
  outstanding: LocationOutstandingSummary;
};

/**
 * What the location still owes, on top of its Location Details. Yellow for
 * an active location; red once the collection letter is available (a deleted
 * location that owes), with the Call summary and the letter beside it, as on
 * its Outstanding Balances page.
 */
export const OutstandingBalanceBanner = ({ locationId, outstanding }: Props) => {
  const navigate = useNavigate();
  const role = useAuth((state) => state.user?.role);
  const isAdmin = role === UserRole.admin;
  const letterAvailable = outstanding.collectionLetterAvailable;
  // The letter is admin only (support would get 403); the summary is not.
  const canDownloadLetter = isAdmin && letterAvailable;
  const canSeeSummary =
    (isAdmin || role === UserRole.support) && letterAvailable;

  const collectionLetter = useCollectionLetter(locationId);
  // From the click until the modal closes; the modal opens once it loads.
  const [isSummaryRequested, setIsSummaryRequested] = useState(false);
  const closeSummary = () => setIsSummaryRequested(false);
  const callSummary = useCollectionLetterSummary(
    locationId,
    isSummaryRequested,
    closeSummary
  );

  const actions = (
    <Div $flex $gap="12px" $columnSM>
      <Button
        $small
        $wSM="100%"
        $gray
        $selected
        onClick={() =>
          navigate(
            ADMIN_RECEIVABLES_LOCATION.replace(':locationId', `${locationId}`)
          )
        }
      >
        View balance
      </Button>
      {canSeeSummary && (
        <Button
          $small
          // Wide enough for the label on one line and the spinner beside it.
          $w="170px"
          $wSM="100%"
          $gray
          $selected
          // $loading already swallows clicks; `disabled` would grey the spinner out.
          $loading={callSummary.isLoading}
          onClick={() => setIsSummaryRequested(true)}
        >
          Call summary
        </Button>
      )}
      {canDownloadLetter && (
        <Button
          $small
          $w="230px"
          $wSM="100%"
          $selected
          $loading={collectionLetter.isPending}
          onClick={collectionLetter.open}
        >
          Collection letter (PDF)
        </Button>
      )}
    </Div>
  );

  return (
    <>
      <NoticeBanner
        critical={letterAvailable}
        title={`Outstanding balance ${displayCentsInDollars(
          outstanding.total
        )}`}
        message={breakdownOf(outstanding)}
        note={notesOf(outstanding, locationId) || null}
        action={actions}
      />
      {canSeeSummary && (
        <CallSummaryModal
          summary={callSummary.summary}
          onClose={closeSummary}
        />
      )}
    </>
  );
};
